import { searchTasks, TaskSearchQuery } from "../taskServices/TaskSearchService";
import { getAllUsersInOrganizationbyId } from "../userServices/UserInfoService";

import UserInfo from "../../interfaces/UserInfo";
import { searchParticipants } from "./ParticipantSearchService";

// 1. UPDATE INTERFACES TO INCLUDE STATUS FLAGS
export type SearchResult =
    | { type: 'task'; id: string; friendlyId: string; title: string; subjectName: string; taskContext: 'Client' | 'Employee'; statusName: string; isDeleted: boolean; }
    | { type: 'participant'; id: string; firstName: string; lastName: string; region: string; waiver: string; deactivated: boolean; }
    | { type: 'employee'; id: string; firstName: string; lastName: string; email: string; deactivated: boolean; };

let cachedEmployees: UserInfo[] | null = null;
let cachedEmployeesAuthToken: string | null = null;

async function getCachedEmployees(authToken: string): Promise<UserInfo[] | null> {
  if (cachedEmployees && cachedEmployeesAuthToken === authToken) {
    return cachedEmployees;
  }
  const employees = await getAllUsersInOrganizationbyId(authToken);
  cachedEmployees = employees;
  cachedEmployeesAuthToken = authToken;
  return employees;
}

export async function globalSearch(authToken: string, searchTerm: string): Promise<SearchResult[]> {
  const taskQuery: TaskSearchQuery = { searchTerm, pageSize: 5 };

  try {
    const [taskResults, participantSearch, employeeResults] = await Promise.all([
      searchTasks(authToken, taskQuery),
      searchParticipants(authToken, { searchTerm, pageSize: 5 }),
      getCachedEmployees(authToken)
    ]);

    // 2. EXTRACT STATUS AND ISDELETED FOR TASKS
    const tasks: SearchResult[] =
      taskResults?.tasks?.map((task: any) => ({
        type: "task",
        id: task.id,
        friendlyId: task.friendlyId,
        title: task.title || "Untitled Task",
        subjectName: task.taskContext === "Employee"
          ? task.subjectEmployee?.displayName ?? ""
          : `${task.participant?.firstName ?? ""} ${task.participant?.lastName ? task.participant.lastName.charAt(0) + "." : ""}`.trim(),
        taskContext: task.taskContext === "Employee" ? "Employee" : "Client",
        statusName: task.status?.name || (typeof task.status === 'string' ? task.status : "") || "",
        isDeleted: !!task.isDeleted
      })) ?? [];

    // 3. EXTRACT DEACTIVATED FLAG FOR PARTICIPANTS
    const participants: SearchResult[] =
      participantSearch?.participants?.map(p => ({
        type: "participant",
        id: String(p.id),
        firstName: p.firstName,
        lastName: p.lastName,
        region: String(p.regionId),
        waiver: p.waiverName ?? "",
        deactivated: !!p.deactivated
      })) ?? [];

    // 4. EXTRACT DEACTIVATED FLAG FOR EMPLOYEES
    const employees: SearchResult[] =
      employeeResults
        ?.filter(e => {
          const s = searchTerm.toLowerCase();
          const name = `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim().toLowerCase();
          const email = (e.email ?? "").toLowerCase();
          const displayName = (e.displayName ?? "").toLowerCase();
          return name.includes(s) || displayName.includes(s) || email.includes(s);
        })
        .map((e: UserInfo) => ({
          type: "employee",
          id: e.id,
          firstName: e.firstName,
          lastName: e.lastName,
          email: e.email,
          deactivated: !!e.deactivated
        })) ?? [];

    return [...employees, ...participants, ...tasks];
  } catch (error) {
    console.error("Error in global search:", error);
    return [];
  }
}
